'use client';

import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';
import 'react-quill-new/dist/quill.snow.css';

interface RichTextEditorProps {
    value: string;
    onChange: (content: string) => void;
    placeholder?: string;
    className?: string;
}

export default function RichTextEditor({ value, onChange, placeholder, className }: RichTextEditorProps) {
    // Dynamic import to avoid SSR issues with Quill
    const ReactQuill = useMemo(() => dynamic(() => import('react-quill-new'), {
        ssr: false,
        loading: () => <div className="h-[200px] w-full bg-background border border-border-main animate-pulse rounded-xl" />
    }), []);

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list',
        'color', 'background'
    ];

    return (
        <div className={`rich-text-editor ${className}`}>
            <ReactQuill
                theme="snow"
                value={value}
                onChange={onChange}
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                className="bg-background rounded-xl overflow-hidden"
            />
            <style jsx global>{`
                .rich-text-editor .ql-toolbar.ql-snow {
                    border: 1px solid var(--color-border-main, #e2e8f0);
                    border-top-left-radius: 12px;
                    border-top-right-radius: 12px;
                    background: var(--color-surface, #ffffff);
                    padding: 8px;
                }
                .rich-text-editor .ql-container.ql-snow {
                    border: 1px solid var(--color-border-main, #e2e8f0);
                    border-bottom-left-radius: 12px;
                    border-bottom-right-radius: 12px;
                    min-height: 150px;
                    font-family: inherit;
                    font-size: 0.875rem;
                }
                .rich-text-editor .ql-editor {
                    min-height: 150px;
                    line-height: 1.6;
                }
                .rich-text-editor .ql-editor ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin-bottom: 1rem;
                }
                .rich-text-editor .ql-editor ol {
                    list-style-type: decimal;
                    padding-left: 1.5rem;
                    margin-bottom: 1rem;
                }
                .rich-text-editor .ql-editor h1, 
                .rich-text-editor .ql-editor h2, 
                .rich-text-editor .ql-editor h3 {
                    font-weight: 700;
                    margin-top: 1rem;
                    margin-bottom: 0.5rem;
                }
                .rich-text-editor .ql-editor.ql-blank::before {
                    color: var(--color-muted, #94a3b8);
                    font-style: italic;
                }
            `}</style>
        </div>
    );
}
